/**
 * Auth surface over Supabase, preserving the legacy mock function names
 * (getMockUser / signIn / signOut) so existing route components keep working.
 */

import type { Session, User } from "@supabase/supabase-js";
import { getSupabase, isSupabaseConfigured } from "@/lib/supabase";

export interface AuthUser {
  id: string;
  email: string;
  name: string;
}

const LEGACY_MOCK_KEY = "csaas.mockUser";

function toAuthUser(user: User | null | undefined): AuthUser | null {
  if (!user || !user.email) return null;
  const metadataName =
    (user.user_metadata?.name as string | undefined) ??
    (user.user_metadata?.full_name as string | undefined);
  return {
    id: user.id,
    email: user.email,
    name: metadataName ?? user.email.split("@")[0] ?? "User",
  };
}

/** Synchronous accessor used by route guards. Returns null on the server. */
export function getMockUser(): AuthUser | null {
  if (typeof window === "undefined") return null;

  // Fast path: read the Supabase session blob directly from localStorage so
  // route guards can decide before the async client finishes hydrating.
  try {
    const raw = window.localStorage.getItem("csaas.supabase.session");
    if (raw) {
      const parsed = JSON.parse(raw) as { user?: User };
      const fromSession = toAuthUser(parsed.user);
      if (fromSession) return fromSession;
    }
  } catch {
    // fall through to legacy mock storage
  }

  // Legacy fallback: anything written by the prior mock auth flow.
  try {
    const raw = window.localStorage.getItem(LEGACY_MOCK_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

/** Async accessor that authoritatively asks Supabase for the current session. */
export async function getCurrentUser(): Promise<AuthUser | null> {
  if (!isSupabaseConfigured()) return getMockUser();
  const { data } = await getSupabase().auth.getSession();
  return toAuthUser(data.session?.user);
}

export async function getAccessToken(): Promise<string | null> {
  if (!isSupabaseConfigured()) return null;
  const { data } = await getSupabase().auth.getSession();
  return data.session?.access_token ?? null;
}

/**
 * Sign in. If a password is provided, uses Supabase email+password.
 * If Supabase is not configured (local dev), falls back to a localStorage stub
 * so the UI remains demoable.
 */
export async function signIn(email: string, password?: string): Promise<AuthUser> {
  if (!isSupabaseConfigured()) {
    const user: AuthUser = {
      id: "mock-user-1",
      email,
      name: email.split("@")[0] || "User",
    };
    window.localStorage.setItem(LEGACY_MOCK_KEY, JSON.stringify(user));
    return user;
  }

  const supabase = getSupabase();
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password: password ?? "",
  });
  if (error || !data.user) {
    throw new Error(error?.message ?? "Sign-in failed");
  }
  const authUser = toAuthUser(data.user);
  if (!authUser) {
    throw new Error("Sign-in succeeded but user payload was empty");
  }
  return authUser;
}

export async function signUp(email: string, password: string, name?: string): Promise<void> {
  if (!isSupabaseConfigured()) {
    await signIn(email, password);
    return;
  }
  const supabase = getSupabase();
  const { error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: name ? { name } : undefined },
  });
  if (error) throw new Error(error.message);
}

export async function signOut(): Promise<void> {
  if (typeof window !== "undefined") {
    window.localStorage.removeItem(LEGACY_MOCK_KEY);
  }
  if (isSupabaseConfigured()) {
    await getSupabase().auth.signOut();
  }
}

export function onAuthChange(cb: (user: AuthUser | null) => void): { unsubscribe: () => void } {
  if (!isSupabaseConfigured()) {
    return { unsubscribe: () => {} };
  }
  const { data } = getSupabase().auth.onAuthStateChange((_event, session: Session | null) => {
    cb(toAuthUser(session?.user));
  });
  return { unsubscribe: () => data.subscription.unsubscribe() };
}
